import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styles from './CreateSchoolForm.module.css';
import InputWithLabel from 'components/InputWithLabel/InputWithLabel';
import Button from 'components/Button/Button';
import { createSchoolSchema, CreateSchoolFormValues } from '../../types/schoolSchemas';
import editProfileIcon from 'assets/edit-profile.png';
import { api } from 'api/axios';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSchool,
    faEnvelope,
    faPhone,
    faUserTie,
    faUserShield,
    faUser,
    faGlobe,
    faCamera,
    faUpload,
    faLock
} from '@fortawesome/free-solid-svg-icons';
import {
    faFacebook,
    faLinkedin,
    faYoutube,
    faInstagram
} from '@fortawesome/free-brands-svg-icons';
import SocialMediaIcon from 'components/SocialMediaIcon/SocialMediaIcon';
import { countries } from 'utils/countries';

const CreateSchoolForm: React.FC = () => {
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<CreateSchoolFormValues>({
        resolver: zodResolver(createSchoolSchema) as any,
    });

    const facebookLink = watch('facebook_link');
    const instagramLink = watch('instagram_link');
    const linkedinLink = watch('linkedin_link');
    const youtubeLink = watch('youtube_link');

    const handleFileSelect = (file: File) => {
        if (file) {
            //Create preview
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);

            // Set form value manually since it's a custom input handling
            setValue('logo', file, { shouldValidate: true });
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0]);
        }
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const [isUploading, setIsUploading] = React.useState(false);

    const onSubmit = async (data: CreateSchoolFormValues) => {
        try {
            console.log('Original Form Data:', data);
            setIsUploading(true);

            let logoUrl = null;
            if (data.logo) {
                const { uploadImage } = await import('api/upload');
                logoUrl = await uploadImage(data.logo as File, 'schools');
            }

            // Construct JSON body according to backend schema
            const payload = {
                name: data.schoolName,
                email: data.schoolEmail,
                contact: data.schoolContact,
                address: data.address,
                country: data.country,

                admin_name: data.adminName,
                admin_username: data.adminUsername,
                admin_email: data.adminEmail,

                principal_name: data.principalName,
                principal_email: data.principalEmail,
                principal_contact: data.principalContact,

                facebook_link: data.facebook_link,
                instagram_link: data.instagram_link,
                youtube_link: data.youtube_link,
                linkedin_link: data.linkedin_link,

                logo_url: logoUrl,
                device_limit: data.device_limit,
                device_expiry: data.device_expiry,
            };

            const response = await api.post('/schools', payload);

            console.log('School created successfully:', response.data);
            alert('School created successfully!');
            navigate('/school-management');
        } catch (error: any) {
            console.error('Error creating school:', error);
            const errorMessage = error.response?.data?.message || 'Failed to create school. Please try again.';
            alert(errorMessage);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className={styles.pageContainer}>
            {/* Page Heading */}
            <div className={styles.heading}>
                <h1 className={styles.title}>Create New School</h1>
                <p className={styles.subtitle}>Set up a new educational institution and assign administrative roles.</p>
            </div>

            {/* Main Form Container */}
            <div className={styles.formContainer}>
                {/* Left Sidebar: Branding & Compliance Cards */}
                <aside className={styles.sidebarWrapper}>
                    <div className={styles.brandingCard}>
                        <h3 className={styles.sidebarTitle}>School Branding</h3>
                        <div
                            className={styles.logoUploadContainer}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp"
                                onChange={handleFileInputChange}
                            />

                            <div className={styles.logoPlaceholder} onClick={handleBrowseClick}>
                                {previewUrl ? (
                                    <img src={previewUrl} alt="School Logo Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div className={styles.logoPlaceholderContent}>
                                        <FontAwesomeIcon icon={faCamera} className={styles.uploadIcon} />
                                        <span className={styles.uploadText}>Click or drag logo here</span>
                                    </div>
                                )}
                            </div>

                            <button
                                type="button"
                                className={styles.uploadButton}
                                onClick={handleBrowseClick}
                            >
                                <FontAwesomeIcon icon={faUpload} /> Upload Logo
                            </button>
                            <p className={styles.fileHint}>SVG, PNG, JPG (max. 800x800px)</p>
                        </div>
                    </div>

                    <div className={styles.complianceCard}>
                        <div className={styles.complianceTitle}>
                            <FontAwesomeIcon icon={faUserShield} /> Compliance Tip
                        </div>
                        <p className={styles.complianceText}>
                            Ensure the school name matches official registration documents for accreditation reports.
                        </p>
                    </div>
                </aside>

                {/* Right Side: Form Fields */}
                <div className={styles.formContent}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {/* Section 1: School Information */}
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <FontAwesomeIcon icon={faSchool} className={styles.sectionIcon} />
                                <h2 className={styles.sectionTitle}>School Information</h2>
                            </div>
                            <div className={styles.grid}>
                                <div className={styles.colSpan2}>
                                    <InputWithLabel
                                        label="School Name"
                                        labelClassName={styles.inputLabel}
                                        className={styles.inputField}
                                        infoTooltip="Enter the full official name of the institution"
                                        placeholder="e.g. Westside Academy of Arts and Sciences"
                                        error={errors.schoolName?.message}
                                        {...register('schoolName')}
                                    />
                                </div>
                                <InputWithLabel
                                    label="School Email"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    placeholder="admin@westside.edu"
                                    error={errors.schoolEmail?.message}
                                    {...register('schoolEmail')}
                                />
                                <InputWithLabel
                                    label="School Contact"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    placeholder="+1 (555) 000-0000"
                                    error={errors.schoolContact?.message}
                                    {...register('schoolContact')}
                                />
                                <div className={styles.colSpan2}>
                                    <InputWithLabel
                                        label="School Address"
                                        labelClassName={styles.inputLabel}
                                        className={styles.inputField}
                                        placeholder="123 Education Lane, City, State, ZIP"
                                        error={errors.address?.message}
                                        {...register('address')}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <label className={styles.inputLabel}>Country</label>
                                    <select 
                                        className={styles.inputField} 
                                        {...register('country')}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #D1D5DB', backgroundColor: '#FFFFFF', fontSize: '0.875rem', outline: 'none' }}
                                    >
                                        <option value="">Select a country...</option>
                                        {countries.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                    {errors.country && <span className={styles.errorText} style={{ color: '#EF4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.country.message}</span>}
                                </div>
                            </div>
                        </section>

                        {/* Section 2: Administrative Contact */}
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <FontAwesomeIcon icon={faUserShield} className={styles.sectionIcon} />
                                <h2 className={styles.sectionTitle}>System Administrator</h2>
                            </div>
                            <div className={`${styles.grid} ${styles.gridThree}`}>
                                <InputWithLabel
                                    label="Admin Name"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    placeholder="Full Name"
                                    error={errors.adminName?.message}
                                    {...register('adminName')}
                                />
                                <InputWithLabel
                                    label="Username"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    infoTooltip="Must be unique within the district domain"
                                    placeholder="j.doe_admin"
                                    error={errors.adminUsername?.message}
                                    {...register('adminUsername')}
                                />
                                <InputWithLabel
                                    label="Admin Email"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    placeholder="admin.contact@email.com"
                                    error={errors.adminEmail?.message}
                                    {...register('adminEmail')}
                                />
                            </div>
                        </section>

                        {/* Section 3: Principal Details */}
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <FontAwesomeIcon icon={faUserTie} className={styles.sectionIcon} />
                                <h2 className={styles.sectionTitle}>Principal Information</h2>
                            </div>
                            <div className={`${styles.grid} ${styles.gridThree}`}>
                                <InputWithLabel
                                    label="Principal Name"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    placeholder="Full Name"
                                    error={errors.principalName?.message}
                                    {...register('principalName')}
                                />
                                <InputWithLabel
                                    label="Principal Contact"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    placeholder="Direct Phone"
                                    error={errors.principalContact?.message}
                                    {...register('principalContact')}
                                />
                                <InputWithLabel
                                    label="Principal Email"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    placeholder="principal@school.edu"
                                    error={errors.principalEmail?.message}
                                    {...register('principalEmail')}
                                />
                            </div>
                        </section>
                        {/* Section 4: Device Management */}
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <FontAwesomeIcon icon={faLock} className={styles.sectionIcon} />
                                <h2 className={styles.sectionTitle}>Device Management</h2>
                            </div>
                            <div className={`${styles.grid} ${styles.gridTwo}`}>
                                <InputWithLabel
                                    label="Device Limit"
                                    type="number"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    infoTooltip="Maximum number of devices this school can register."
                                    placeholder="e.g. 50"
                                    error={errors.device_limit?.message}
                                    {...register('device_limit')}
                                />
                                <InputWithLabel
                                    label="License Expiry Date"
                                    type="date"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    infoTooltip="When does the school's device license expire?"
                                    error={errors.device_expiry?.message}
                                    {...register('device_expiry')}
                                />
                            </div>
                        </section>

                        {/* Section 5: Social Media Links */}
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <FontAwesomeIcon icon={faGlobe} className={styles.sectionIcon} />
                                <h2 className={styles.sectionTitle}>Social Media Links (Optional)</h2>
                            </div>
                            <div className={styles.grid}>
                                <InputWithLabel
                                    label="Facebook Link"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    placeholder="https://facebook.com/yourschool"
                                    error={errors.facebook_link?.message}
                                    {...register('facebook_link')}
                                />
                                <InputWithLabel
                                    label="Instagram Link"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    placeholder="https://instagram.com/yourschool"
                                    error={errors.instagram_link?.message}
                                    {...register('instagram_link')}
                                />
                                <InputWithLabel
                                    label="YouTube Link"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    placeholder="https://youtube.com/c/yourschool"
                                    error={errors.youtube_link?.message}
                                    {...register('youtube_link')}
                                />
                                <InputWithLabel
                                    label="LinkedIn Link"
                                    labelClassName={styles.inputLabel}
                                    className={styles.inputField}
                                    placeholder="https://linkedin.com/company/yourschool"
                                    error={errors.linkedin_link?.message}
                                    {...register('linkedin_link')}
                                />
                            </div>
                        </section>

                        {/* Form Actions */}
                        <div className={styles.actions}>
                            <button type="button" className={styles.cancelButton} onClick={() => navigate('/school-management')}>
                                Cancel
                            </button>
                            <button type="submit" className={styles.submitButton} disabled={isSubmitting || isUploading}>
                                {isSubmitting || isUploading ? 'Creating...' : 'Create School Profile'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Footer Help text */}
            <div className={styles.footerHelp}>
                <span className={styles.helpText}>
                    <FontAwesomeIcon icon={faLock} className={styles.helpIcon} />
                    All institutional data is secured and managed according to District Compliance policies.
                </span>
            </div>
        </div>
    );
};

export default CreateSchoolForm;
